// 我的 tab —— 账号 + 我的相册 + 最近观看 + 报修/投诉/日志（按 Android 截图复刻）
import { useEffect, useState } from 'react';
import { Typography, Avatar, Button } from 'antd';
import {
	PictureOutlined,
	ClockCircleOutlined,
	ToolOutlined,
	FileSearchOutlined,
	CustomerServiceOutlined,
	MessageOutlined,
	BookOutlined,
	UserOutlined,
	RightOutlined,
	LogoutOutlined,
	SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.js';
import LocaleSwitcher from '../components/LocaleSwitcher.js';
import { getRecentRecordsWithApacHydration } from '../api/recent.js';
import { fetchUserInfo, pickName, pickAvatar, type UlucuUserInfo } from '../api/user.js';
import { getRecentRecords, type RecentRecord } from '../utils/recent-records.js';
import { useRegion } from '../hooks/useRegion.js';
import {
	ActionList,
	MobileRootPage,
	NativeMirrorPage,
	RegionTag,
	RootHeader,
	SectionBlock,
	SummaryStrip,
} from '../components/MobileRootScaffold.js';
import { mobileColor, mobileFontSize, mobileFontWeight } from '../theme/mobile-typography.js';

interface ItemDef {
	key: string;
	i18nKey: string;
	Icon: React.ComponentType<{ style?: React.CSSProperties }>;
	color: string;
	to: string;
}

export default function ProfileTab() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const user = useAuthStore((s) => s.user);
	const clearAuth = useAuthStore((s) => s.clearAuth);

	const [info, setInfo] = useState<UlucuUserInfo | null>(null);
	const [recent, setRecent] = useState<RecentRecord[]>([]);

	useEffect(() => {
		void fetchUserInfo().then(setInfo).catch(() => setInfo(null));
		if (region === 'apac') {
			void getRecentRecordsWithApacHydration().then(setRecent).catch(() => setRecent(getRecentRecords()));
		} else {
			setRecent(getRecentRecords());
		}
	}, [region]);

	const activityItems: ItemDef[] = [
		{ key: 'album',     i18nKey: 'mtab.profile.album',       Icon: PictureOutlined,     color: '#5B8DEF', to: '/m/album' },
		{ key: 'recent',    i18nKey: 'mtab.profile.recent',      Icon: ClockCircleOutlined, color: '#FFA940', to: '/m/recent' },
		{ key: 'changelog', i18nKey: 'mtab.profile.changelog',   Icon: BookOutlined,        color: '#5B8DEF', to: '/m/changelog' },
	];
	const supportItems: ItemDef[] = [
		{ key: 'repair',    i18nKey: 'mtab.profile.repair',      Icon: ToolOutlined,            color: '#5DD5C4', to: '/m/repair' },
		{ key: 'repair_log',i18nKey: 'mtab.profile.repair_log',  Icon: FileSearchOutlined,      color: '#5B8DEF', to: '/m/repair-list' },
		{ key: 'hotline',   i18nKey: 'mtab.profile.hotline',     Icon: CustomerServiceOutlined, color: '#FFA940', to: '/m/hotline' },
		{ key: 'feedback',  i18nKey: 'mtab.profile.feedback',    Icon: MessageOutlined,         color: '#FF6347', to: '/m/feedback' },
	];

	const onLogout = () => {
		clearAuth();
		navigate('/login', { replace: true });
	};

	const displayName = pickName(info) || user?.account || t('mtab.profile.guest');
	const avatarUrl = pickAvatar(info);

	if (region === 'apac') {
		const nativeName = nativePrimaryName(info, user?.account, displayName);
		const nativeInfo = nativeSecondaryInfo(info, nativeName);
		const latestRecent = recent[0];

		return (
			<NativeMirrorPage style={{ background: '#fff', paddingBottom: 0 }}>
				<button
					type="button"
					onClick={() => navigate('/m/profile-edit')}
					style={{
						width: '100%',
						height: 166,
						border: 0,
						padding: '0 24px',
						background: 'linear-gradient(135deg, #ffa62e 0%, #ffcc4d 58%, #ffe277 100%)',
						display: 'flex',
						alignItems: 'center',
						gap: 22,
						textAlign: 'start',
						cursor: 'pointer',
					}}
				>
					<div
						style={{
							width: 72,
							height: 72,
							borderRadius: '50%',
							background: 'rgba(255,255,255,0.88)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							overflow: 'hidden',
							flex: '0 0 auto',
						}}
					>
						<Avatar
							size={72}
							src={avatarUrl}
							icon={!avatarUrl && <UserOutlined />}
							style={{ background: avatarUrl ? undefined : '#f2f2f2', color: '#999' }}
						/>
					</div>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ color: '#fff', fontSize: mobileFontSize.topBar, fontWeight: mobileFontWeight.medium, lineHeight: 1.2, textShadow: '0 1px 1px rgba(0,0,0,0.12)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{nativeName}
						</div>
						{nativeInfo && (
							<div style={{ marginTop: 12, color: '#fff', fontSize: 14, lineHeight: 1.2, textShadow: '0 1px 1px rgba(0,0,0,0.12)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{nativeInfo}
							</div>
						)}
					</div>
					<RightOutlined style={{ color: '#fff', fontSize: 20, flex: '0 0 auto' }} />
				</button>

				<div style={{ background: '#fff' }}>
					<NativeMyRow title={t('native.profile.my_album')} icon={<PictureOutlined />} onClick={() => navigate('/m/album')} first />
					<NativeMyRow title={t('native.profile.watch_history')} icon={<ClockCircleOutlined />} onClick={() => navigate('/m/recent')} />
					{latestRecent && (
						<div style={{ height: 84, padding: '0 6px 12px 6px', display: 'flex', alignItems: 'stretch' }}>
							<button
								type="button"
								onClick={() => navigate('/m/recent')}
								style={{
									width: 108,
									border: 0,
									background: 'transparent',
									padding: 0,
									margin: 0,
									cursor: 'pointer',
									textAlign: 'center',
								}}
							>
								<div style={{ height: 54, margin: '0 6px', background: '#d8d8d8', overflow: 'hidden' }}>
									{recentThumbSrc(latestRecent) ? (
										<img src={recentThumbSrc(latestRecent)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
									) : (
										<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777', fontSize: 12, padding: 4 }}>
											<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{latestRecent.deviceLabel}</span>
										</div>
									)}
								</div>
								<div style={{ marginTop: 6, color: '#333', fontSize: 13, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{latestRecent.storeName}/{latestRecent.deviceLabel}
								</div>
							</button>
						</div>
					)}
					<div style={{ height: 12, background: '#f5f5f5' }} />
					<NativeMyRow title={t('native.profile.online_repair')} icon={<ToolOutlined />} onClick={() => navigate('/m/repair')} first />
					<NativeMyRow title={t('native.profile.repair_record')} icon={<FileSearchOutlined />} onClick={() => navigate('/m/repair-list')} />
					<div style={{ height: 12, background: '#f5f5f5' }} />
					<NativeMyRow title={t('native.profile.setting')} icon={<SettingOutlined />} onClick={() => navigate('/m/settings')} first />
				</div>
			</NativeMirrorPage>
		);
	}

	return (
		<MobileRootPage>
			<RootHeader
				icon={<UserOutlined />}
				title={t('tabbar.account')}
				subtitle={t('root.account.subtitle')}
				meta={<RegionTag region={region} />}
			/>

			<SummaryStrip
				items={[
					{ key: 'region', label: t('root.account.region'), value: 'CN' },
					{ key: 'language', label: t('root.account.language'), value: i18n.language },
					{ key: 'recent', label: t('root.account.recent_count'), value: recent.length },
					{ key: 'support', label: t('root.account.support_count'), value: supportItems.length },
				]}
			/>

			<SectionBlock title={t('root.account.profile')}>
				<button
					type="button"
					onClick={() => navigate('/m/profile-edit')}
					style={{
						width: '100%',
						border: '1px solid #e5e7eb',
						background: '#fff',
						borderRadius: 8,
						padding: 16,
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						textAlign: 'start',
						cursor: 'pointer',
					}}
				>
					<Avatar
						size={56}
						src={avatarUrl}
						icon={!avatarUrl && <UserOutlined />}
						style={{ background: avatarUrl ? undefined : '#e8e8e8', color: '#999' }}
					/>
					<div style={{ flex: 1, minWidth: 0 }}>
						<Typography.Text style={{ fontSize: 15, fontWeight: 500 }} ellipsis>
							{displayName}
						</Typography.Text>
						<div>
							<Typography.Text type="secondary" style={{ fontSize: 13 }}>
								{t('mtab.profile.subtitle')}
							</Typography.Text>
						</div>
					</div>
					<RightOutlined style={{ color: '#9aa3af', fontSize: 12 }} />
				</button>
			</SectionBlock>

			<SectionBlock title={t('root.account.preferences')}>
				<ActionList
					items={[
						{
							key: 'edit',
							title: t('mtab.profile.edit_profile'),
							subtitle: t('root.account.profile_hint'),
							icon: <UserOutlined />,
							onClick: () => navigate('/m/profile-edit'),
						},
						{
							key: 'language',
							title: t('root.account.language'),
							subtitle: t('root.account.language_hint'),
							icon: <MessageOutlined />,
							trailing: <LocaleSwitcher />,
						},
						{
							key: 'settings',
							title: t('mtab.profile.settings'),
							subtitle: t('mtab.profile.settings_hint'),
							icon: <SettingOutlined />,
							onClick: () => navigate('/m/settings'),
						},
					]}
				/>
			</SectionBlock>

			<SectionBlock title={t('root.account.activity')}>
				<ActionList
					items={activityItems.map((item) => ({
						key: item.key,
						title: t(item.i18nKey),
						icon: <item.Icon style={{ color: item.color }} />,
						onClick: () => navigate(item.to),
					}))}
				/>
				{recent.length > 0 && (
					<div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '12px 0 0' }}>
						{recent.slice(0, 4).map((r) => (
							<div
								key={`${r.deviceSn}-${r.channel}`}
								onClick={() => navigate(`/m/player?storeId=${r.storeId}&storeName=${encodeURIComponent(r.storeName)}&deviceSn=${r.deviceSn}&deviceLabel=${encodeURIComponent(r.deviceLabel)}&channel=${r.channel}`)}
								style={{
									flex: '0 0 120px',
									aspectRatio: '16 / 9',
									borderRadius: 8,
									overflow: 'hidden',
									background: '#000',
									position: 'relative',
									cursor: 'pointer',
								}}
							>
								{recentThumbSrc(r) ? (
									<img src={recentThumbSrc(r)} alt={r.deviceLabel}
										style={{ width: '100%', height: '100%', objectFit: 'cover' }}
									/>
								) : (
									<div style={{
										width: '100%', height: '100%',
										display: 'flex', alignItems: 'center', justifyContent: 'center',
										color: '#666', fontSize: 11,
									}}>
										{r.deviceLabel.slice(0, 6)}
									</div>
								)}
								<div style={{
									position: 'absolute',
									bottom: 0, left: 0, right: 0,
									padding: '2px 6px',
									background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
									color: '#fff',
									fontSize: 10,
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}>
									{r.storeName} / {r.deviceLabel}
								</div>
							</div>
						))}
					</div>
				)}
			</SectionBlock>

			<SectionBlock title={t('root.account.support')}>
				<ActionList
					items={supportItems.map((item) => ({
						key: item.key,
						title: t(item.i18nKey),
						icon: <item.Icon style={{ color: item.color }} />,
						onClick: () => navigate(item.to),
					}))}
				/>
			</SectionBlock>

			<Button
				danger
				block
				icon={<LogoutOutlined />}
				onClick={onLogout}
				style={{ marginTop: 6, height: 44, borderRadius: 8, borderColor: '#f2b8b5', color: '#b42318' }}
			>
				{t('auth.logout')}
			</Button>
		</MobileRootPage>
	);
}

function NativeMyRow({
	title,
	icon,
	onClick,
	first,
}: {
	title: string;
	icon: React.ReactNode;
	onClick: () => void;
	first?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				width: '100%',
				height: 58,
				border: 0,
				borderTop: first ? 0 : '1px solid #eeeeee',
				background: '#fff',
				padding: '0 12px',
				display: 'flex',
				alignItems: 'center',
				gap: 12,
				textAlign: 'start',
				cursor: 'pointer',
			}}
		>
			<span style={{ width: 24, height: 24, color: '#ff8a34', fontSize: 21, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				{icon}
			</span>
			<span style={{ flex: 1, minWidth: 0, color: mobileColor.text, fontSize: mobileFontSize.title, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
				{title}
			</span>
			<RightOutlined style={{ color: '#c8c8c8', fontSize: 15 }} />
		</button>
	);
}

function nativePrimaryName(info: UlucuUserInfo | null, account: string | undefined, fallback: string) {
	const accountName = (info?.account || account || '').split('@')[0];
	return String(info?.realname || accountName || fallback);
}

function recentThumbSrc(record: RecentRecord): string | undefined {
	return record.thumbDataUrl || record.thumbUrl;
}

function nativeSecondaryInfo(info: UlucuUserInfo | null, primary: string) {
	const candidates = [
		info?.name,
		info?.nick_name,
		info?.nickname,
		info?.nickName,
		info?.user_info,
		info?.info,
		info?.remark,
		info?.customer_name,
	];
	for (const value of candidates) {
		if (typeof value !== 'string') continue;
		const text = value.trim();
		if (text && text !== primary) return text;
	}
	return '';
}
